import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import WhatIfCalculatorPres from '../presentational/WhatIfCalculatorPres';
import {
  addEmployee,
  setEmployeeValue,
  setOutputCurrency,
  getCalculations,
  removeSelectedEmployees,
  clearEmployees,
  setEmployeesFromCSV,
  getCountriesDictionary,
} from '../state/whatIfActions';
import moment from 'moment';
import intl from 'react-intl-universal';
import { withSnackbar } from 'notistack';
import Spinner from 'src/ui/library/Spinner/Spinner';

class WhatIfCalculatorContainer extends React.Component {
  static propTypes = {
    enqueueSnackbar: PropTypes.func.isRequired,
  };

  state = {
    keys: ['homeCountry', 'hostCountry', 'level', 'maritalStatus', 'noOfEmployees', 'startDate', 'endDate'],
    currencySet: ['USD'],
    openDialog: false,
    params: [],
    showSelectionButtons: false,
  };

  componentDidMount() {
    this.props.getCountriesDictionary();
  }

  onCalculate = () => {
    this.calculate(this.props.employees);
  };

  calculate = employees => {
    const { lookups, countriesDictionary } = this.props;
    let error = false;
    const params = [];

    let index = 1;
    Object.keys(employees).forEach(i => {
      const employee = employees[i];
      const isValid = this.isEmployeeValid(employee, index++);
      const sheetName = employee.homeCountry + 'To' + employee.hostCountry;
      if (isValid) {
        params.push({
          sheet: sheetName,
          hostCountry: countriesDictionary[employee.homeCountry].countries[employee.hostCountry].id,
          homeCountry: countriesDictionary[employee.homeCountry].id,
          classification: employee.level,
          policy: employee.policy,
          maritalStatus: employee.maritalStatus,
          familySize: Number(employee.familySize),
          numChildren: employee.numOfSchoolAgedChildren ? Number(employee.numOfSchoolAgedChildren) : 0,
          numEmployees: Number(employee.noOfEmployees),
          periodToBeCalculatedStart: moment(employee.startDate, 'DD-MMM-YYYY')
            .utc()
            .format(),
          periodToBeCalculatedEnd: moment(employee.endDate, 'DD-MMM-YYYY')
            .utc()
            .format(),
          salary: Number(employee.salary),
        });
      } else {
        error = true;
      }
    });

    if (error) {
      return;
    }

    let currencySet = ['USD'];
    Object.keys(employees).forEach(i => {
      const employee = employees[i];
      const homeCurrency = lookups.currencies[employee.homeCountry.toUpperCase()];
      const hostCurrency = lookups.currencies[employee.hostCountry.toUpperCase()];

      if (homeCurrency && !currencySet.toString().includes(homeCurrency)) {
        currencySet.push(homeCurrency);
      }
      if (hostCurrency && !currencySet.toString().includes(hostCurrency)) {
        currencySet.push(hostCurrency);
      }
    });
    currencySet.sort();

    this.setState({
      currencySet: currencySet,
      openDialog: true,
      params: params,
    });
  };

  continueCalculate = () => {
    let { params } = this.state;
    let { outputCurrency } = this.props;
    params.forEach(param => {
      param.outputCurrency = outputCurrency.value;
    });

    this.props.getCalculations(params);
  };

  onChangeCurrency = event => {
    this.props.setOutputCurrency({ label: event.target.label, value: event.target.value });
  };

  onDialogClose = () => {
    this.setState({
      openDialog: false,
    });
  };

  onChange = (id, target) => {
    const { countriesDictionary } = this.props;

    if (target.name === 'homeCountry') {
      this.props.setEmployeeValue(id, 'hostCountry', null);
      this.populateCountriesList(countriesDictionary, target.label, 'host');
    }
    this.props.setEmployeeValue(id, target.name, target.value);
  };

  isEmployeeValid = (employee, index) => {
    const { enqueueSnackbar, countriesDictionary } = this.props;

    let isValid = true;
    let errorMsg = ['Employee Type ' + index];
    let fields = [];
    const isEmpty = Object.values(employee).every(x => x === null || x === '' || x === undefined);

    if (!isEmpty) {
      if (employee.homeCountry && employee.hostCountry) {
        const homeCountry = countriesDictionary[employee.homeCountry];
        if (!homeCountry) {
          errorMsg.push(intl.get('whatif.calculator.messages.notifyUnsupportedCountries'));
          isValid = false;
        } else {
          const hostCountry = countriesDictionary[employee.homeCountry].countries[employee.hostCountry];
          if (!hostCountry) {
            errorMsg.push(intl.get('whatif.calculator.messages.notifyUnsupportedCountries'));
            isValid = false;
          }
        }
      } else {
        // errorMsg.push(intl.get('whatif.calculator.messages.countriesRequired'));
        fields.push('Home/Host country');
        isValid = false;
      }
      if (employee.level === undefined || this.props.lookups.levelsList.indexOf(employee.level) < 0) {
        // errorMsg.push(intl.get('whatif.calculator.messages.levelRequired'));
        fields.push('Level');
        isValid = false;
      }
      if (employee.maritalStatus === undefined || this.props.lookups.maritalStatusList.indexOf(employee.maritalStatus) < 0) {
        // errorMsg.push(intl.get('whatif.calculator.messages.maritalStatusRequired'));
        fields.push('Marital Status');
        isValid = false;
      }
      if (employee.noOfEmployees === undefined || employee.noOfEmployees < 1) {
        // errorMsg.push(intl.get('whatif.calculator.messages.numEmployeeRequired'));
        fields.push('Number of Employees');
        isValid = false;
      }
      if (
        employee.endDate === undefined ||
        employee.startDate === undefined ||
        !moment(employee.startDate, 'D-MMM-YYYY').isValid() ||
        !moment(employee.endDate, 'D-MMM-YYYY').isValid()
      ) {
        // errorMsg.push(intl.get('whatif.calculator.messages.datesRequired'));
        fields.push('Start/End date');
        isValid = false;
      } else if (moment(employee.startDate, 'D-MMM-YYYY') >= moment(employee.endDate, 'D-MMM-YYYY')) {
        errorMsg.push(intl.get('whatif.calculator.messages.startBeforeEndDate'));
        isValid = false;
      }
    } else {
      errorMsg.push(intl.get('whatif.calculator.messages.emptyRow'));
      isValid = false;
    }

    if (!isValid) {
      if (fields.length > 0) {
        errorMsg.push('Required Fields: ' + fields.join(', '));
      }
      enqueueSnackbar(<div style={{ 'white-space': 'pre-line' }}>{errorMsg.join('\n')}</div>, { variant: 'error' });
    }

    return isValid;
  };

  isFileDataValid = data => {
    let isValid = true;
    let index = 1;
    Object.keys(data).forEach(i => {
      const employee = data[i];
      if (this.isEmployeeValid(employee, index++) === false) {
        isValid = false;
      }
    });

    return isValid;
  };

  handleFileUpload = data => {
    const { enqueueSnackbar } = this.props;

    if (this.isFileDataValid(data)) {
      let filteredData = {};

      Object.keys(data).forEach(i => {
        const employee = data[i];
        const isEmpty = Object.values(employee).every(x => x === null || x === '');
        if (!isEmpty) {
          filteredData[i] = employee;
        }
      });

      if (Object.keys(filteredData).length > 0) {
        this.props.setEmployeesFromCSV(filteredData);
        this.calculate(filteredData);
      } else {
        enqueueSnackbar(intl.get('whatif.calculator.messages.emptyFile'), { variant: 'error' });
      }
    }
  };

  render() {
    if (this.props.isLoading || !this.props.countriesDictionary) {
      return (
        <div style={{ marginTop: '10%' }}>
          <Spinner size={130} style={{ marginTop: '50px' }} />
        </div>
      );
    }

    return (
      <WhatIfCalculatorPres
        {...this.props}
        {...this.state}
        onAddEmployee={this.props.addEmployee}
        onCalculate={this.onCalculate}
        onChange={this.onChange}
        onDelete={this.props.removeSelectedEmployees}
        onCancel={this.props.clearEmployees}
        handleFileUpload={this.handleFileUpload}
        onDialogClose={this.onDialogClose}
        continueCalculate={this.continueCalculate}
        onChangeCurrency={this.onChangeCurrency}
      />
    );
  }
}

const mapStateToProps = state => ({
  employees: state.whatIf.employees,
  lookups: state.whatIf.lookups,
  results: state.whatIf.results,
  isLoading: state.whatIf.isLoadingCalculations,
  countriesDictionary: state.whatIf.countriesDictionary,
  outputCurrency: state.whatIf.outputCurrency,
});

const mapDispatchToProps = {
  addEmployee,
  setEmployeeValue,
  setOutputCurrency,
  push,
  getCalculations,
  removeSelectedEmployees,
  clearEmployees,
  setEmployeesFromCSV,
  getCountriesDictionary,
};

export default connect(mapStateToProps, mapDispatchToProps)(withSnackbar(WhatIfCalculatorContainer));
